import { modeleMail } from './modeles-mail.js';
/**
 * Mail de tracking, composé ici plutôt que dans n8n.
 *
 * Le scénario n8n faisait trois choses : choisir la langue (un nœud Switch à
 * deux sorties, anglais et français, tout le reste tombant en français),
 * assembler un corps HTML par langue, puis y coller la signature. Les trois
 * tiennent dans ce fichier, en fonctions pures : la composition est testable
 * sans réseau, sans Outlook et sans instance n8n, et une faute de frappe dans
 * un modèle se voit en test plutôt qu'en production.
 *
 * Ce qui change par rapport au scénario :
 *
 * - **cinq langues** au lieu de deux (fr, en, it, es, de) ;
 * - **le lien pointe le transporteur**, pas un agrégateur : l'URL vient de
 *   `urlSuiviTransporteur`, construite à partir du numéro et du transporteur ;
 * - **le cas « pas de page de suivi connue » est traité** : le mail donne alors
 *   le numéro et le transporteur en clair, au lieu d'un lien mort ;
 * - **les valeurs sont échappées** avant d'entrer dans le HTML.
 */
/** Langues pour lesquelles un modèle existe. `fr` sert de repli. */
export const LANGUES_MAIL = ['fr', 'en', 'it', 'es', 'de'];
/**
 * Langue appliquée quand celle du client est absente ou inconnue.
 *
 * C'est le comportement du scénario n8n : sa sortie de repli (`fallbackOutput`)
 * était branchée sur le texte français.
 */
export const LANGUE_MAIL_DEFAUT = 'fr';
export const LIBELLE_LANGUE = {
    fr: 'Français',
    en: 'Anglais',
    it: 'Italien',
    es: 'Espagnol',
    de: 'Allemand',
};
/**
 * Ramène une langue de commande à un modèle existant.
 *
 * La base contient aussi bien `fr` que `fr-fr`, `FR` ou `it` : seules les deux
 * premières lettres comptent. Une langue absente de la liste — ou absente tout
 * court — retombe sur le français, jamais sur un mail vide.
 */
export function normaliserLangueMail(langue) {
    const code = String(langue ?? '').trim().toLowerCase().slice(0, 2);
    return LANGUES_MAIL.includes(code)
        ? code
        : LANGUE_MAIL_DEFAUT;
}
/** `true` si la langue du client a bien un modèle à elle. Sert à le dire au journal. */
export function langueMailReconnue(langue) {
    const code = String(langue ?? '').trim().toLowerCase().slice(0, 2);
    return LANGUES_MAIL.includes(code);
}
/** Échappement HTML des valeurs interpolées : une référence ne doit jamais fermer une balise. */
export function echapperHtml(valeur) {
    return String(valeur ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/**
 * Échappement d'une URL destinée à un attribut `href`.
 *
 * Seuls `http` et `https` passent : une URL de suivi vient de nos propres
 * modèles, mais `expedition.urlSuivi` est un champ de base qui a pu être saisi
 * ailleurs, et un `javascript:` dans un mail n'a rien à y faire.
 */
export function urlSure(url) {
    const brut = String(url ?? '').trim();
    if (!brut)
        return null;
    if (!/^https?:\/\//i.test(brut))
        return null;
    return echapperHtml(brut);
}
const MODELES = {
    fr: {
        objet: (ref) => `Expédition commande ${ref}`,
        bonjour: 'Bonjour,',
        expediee: 'Votre commande est expédiée.',
        lienIntro: 'Vous trouverez ci-contre le lien de suivi :',
        lienTexte: 'Cliquez ici',
        delaiActivation: 'Le lien de suivi peut mettre 1 à 2 jours pour être actif.',
        numeroIntro: 'Votre numéro de suivi :',
        transporteurLabel: 'Transporteur :',
        reference: 'Référence à indiquer si besoin :',
        disposition: 'Restant à votre disposition pour tout complément d’information.',
        salutations: 'Cordialement',
    },
    en: {
        objet: (ref) => `Tracking order ${ref}`,
        bonjour: 'Hello,',
        expediee: 'Your order has been shipped.',
        lienIntro: 'Please find the tracking link here:',
        lienTexte: 'Click here',
        delaiActivation: 'The tracking link may take 1 to 2 days to become active.',
        numeroIntro: 'Your tracking number:',
        transporteurLabel: 'Carrier:',
        reference: 'Reference to provide if needed:',
        disposition: 'We remain at your disposal for any further information.',
        salutations: 'Best regards',
    },
    it: {
        objet: (ref) => `Spedizione ordine ${ref}`,
        bonjour: 'Buongiorno,',
        expediee: 'Il suo ordine è stato spedito.',
        lienIntro: 'Di seguito il link per seguire la spedizione:',
        lienTexte: 'Clicchi qui',
        delaiActivation: 'Il link di tracciamento può richiedere 1 o 2 giorni per attivarsi.',
        numeroIntro: 'Il suo numero di spedizione:',
        transporteurLabel: 'Trasportatore:',
        reference: 'Riferimento da indicare in caso di necessità:',
        disposition: 'Restiamo a sua disposizione per ogni ulteriore informazione.',
        salutations: 'Cordiali saluti',
    },
    es: {
        objet: (ref) => `Envío del pedido ${ref}`,
        bonjour: 'Buenos días,',
        expediee: 'Su pedido ha sido enviado.',
        lienIntro: 'Aquí tiene el enlace de seguimiento:',
        lienTexte: 'Haga clic aquí',
        delaiActivation: 'El enlace de seguimiento puede tardar 1 o 2 días en activarse.',
        numeroIntro: 'Su número de seguimiento:',
        transporteurLabel: 'Transportista:',
        reference: 'Referencia a indicar si es necesario:',
        disposition: 'Quedamos a su disposición para cualquier información adicional.',
        salutations: 'Atentamente',
    },
    de: {
        objet: (ref) => `Versand Ihrer Bestellung ${ref}`,
        bonjour: 'Guten Tag,',
        expediee: 'Ihre Bestellung wurde versandt.',
        lienIntro: 'Hier finden Sie den Link zur Sendungsverfolgung:',
        lienTexte: 'Hier klicken',
        delaiActivation: 'Der Link zur Sendungsverfolgung kann 1 bis 2 Tage brauchen, bis er aktiv ist.',
        numeroIntro: 'Ihre Sendungsnummer:',
        transporteurLabel: 'Spediteur:',
        reference: 'Referenz, falls erforderlich anzugeben:',
        disposition: 'Für weitere Informationen stehen wir Ihnen gerne zur Verfügung.',
        salutations: 'Mit freundlichen Grüßen',
    },
};
/**
 * Signature Mosaik, reprise telle quelle du nœud « Add signature ».
 *
 * Elle est figée ici volontairement : c'est un bloc de mise en page produit par
 * WiseStamp, pas un texte à retoucher à la main. Le jour où elle doit changer,
 * elle changera à un seul endroit — et le pixel de suivi WiseStamp en fin de
 * bloc pourra être retiré à cette occasion, il ne sert qu'à l'outil qui l'a
 * généré.
 *
 * Ce qui a été retiré des mails, c'est la ligne de signature en TEXTE que les
 * modèles posaient juste au-dessus — « Mosaik Floor — service administratif »,
 * « Mosaik Floor — Administration », « Mosaik Floor — service expédition ». Elle
 * répétait en clair ce que ce bloc dit déjà, et mieux.
 */
export const SIGNATURE_MOSAIK_HTML = `<div dir='ltr'><table style='border-collapse:collapse;'><tbody><tr><td style='font-size:0;height:12px;line-height:0;'></td></tr><tr><td><table cellpadding='0' cellspacing='0' border='0' style='width:100%;' width='100%'><tbody><tr><td><table cellpadding='0' cellspacing='0' style='border-collapse:collapse;line-height:1.15;'><tbody><tr><td style='vertical-align:top;padding:0.01px 14px 0.01px 1px;width:65px;text-align:center;'><img border='0' src='https://d36urhup7zbd7q.cloudfront.net/u/wDgMjaPwrwn/cd5bcd27-be59-4f26-868c-13da502c94ed__214x214__.png' height='65' width='65' alt='photo' style='width:65px;vertical-align:middle;border-radius:0;height:65px;border:0;display:block;'><br></td><td valign='top' style='padding:0.01px 0.01px 0.01px 14px;vertical-align:top;border-left:solid 1px #BDBDBD;'><table cellpadding='0' cellspacing='0' style='border-collapse:collapse;'><tbody><tr><td style='padding:0.01px;'><span  style='font-family:Arial;font-size:16px;font-weight:bold;color:#646464;letter-spacing:0;white-space:nowrap;'>Service Administratif</span><br><span  style='font-family:Arial;font-size:13px;font-weight:bold;color:#646464;white-space:nowrap;'>MOSAIK CREATION</span><br></td></tr><tr><td><table cellpadding='0' cellspacing='0' style='border-collapse:collapse;'><tbody><tr><td nowrap='nowrap' width='297' style='padding-top:14px;white-space:nowrap;width:297px;font-family:Arial;'><span  style='white-space:nowrap;'><span style='font-size:12px;'><a href='tel:+330481918374' style='font-family:Arial;'><span style='font-family:Arial;color:rgb(33,33,33);white-space:nowrap;'>+33 (0) 4 81 91 83 74</span></a></span></span><br></td></tr><tr><td nowrap='nowrap' width='311' style='padding-top:8px;white-space:nowrap;width:311px;font-family:Arial;'><span  style='white-space:nowrap;'><span style='font-size:12px;'><a href='https://www.mosaik-floor.fr' style='font-family:Arial;'><span style='font-family:Arial;color:rgb(33,33,33);white-space:nowrap;'>www.mosaik-floor.fr</span></a>  |  contact</span><a href='mailto:marlene@mosaik-floor.fr' style='font-family:Arial;'><span  style='line-height:120%;font-family:Arial;color:rgb(33,33,33);white-space:nowrap;'><span style='font-size:12px;'>@mosaik-floor.fr</span></span></a></span><br></td></tr></tbody></table></td></tr><tr><td style='padding:14px 0.01px 0.01px 0.01px;'><table border='0' cellpadding='0' cellspacing='0'><tbody><tr><td align='left' style='padding-right:6px;text-align:center;padding-top:0;'><a href='https://www.facebook.com/MosaikFloorFB'><img width='24' height='24' src='https://cdn.gifo.wisestamp.com/s/fb/3b5998/48/0/background.png' style='float:left;border:none;' border='0' alt='facebook'></a><br></td><td align='left' style='padding-right:6px;text-align:center;padding-top:0;'><a href='https://www.instagram.com/mosaikcreation/'><img width='24' height='24' src='https://cdn.gifo.wisestamp.com/s/inst/E4405F/48/0/background.png' style='float:left;border:none;' border='0' alt='instagram'></a><br></td><td align='left' style='padding-right:6px;text-align:center;padding-top:0;'><a href='https://www.linkedin.com/company/30637620/'><img width='24' height='24' src='https://cdn.gifo.wisestamp.com/s/ld/0077b5/48/0/background.png' style='float:left;border:none;' border='0' alt='linkedin'></a><br></td><td align='left' style='padding-right:6px;text-align:center;padding-top:0;'><a href='https://www.youtube.com/@mosaikfloor8698'><img width='24' height='24' src='https://cdn.gifo.wisestamp.com/s/yt/ff0000/48/0/background.png' style='float:left;border:none;' border='0' alt='youtube'></a><br></td><td align='left' style='padding-right:6px;text-align:center;padding-top:0;'><a href='https://www.pinterest.fr/mosaikfloor/'><img width='24' height='24' src='https://cdn.gifo.wisestamp.com/s/ptrst/bd081c/48/0/background.png' style='float:left;border:none;' border='0' alt='pinterest'></a><br></td></tr></tbody></table></td></tr></tbody></table></td></tr></tbody></table></td></tr></tbody></table></td></tr></tbody></table><br></div>`;
/**
 * Compose le mail de tracking en texte.
 *
 * Même contenu que la version HTML, sous la forme que l'utilisateur peut relire
 * et corriger — c'est devenu la seule façon dont ce mail part, aucun mail
 * ne partant sans avoir été vu.
 *
 * L'URL de suivi est écrite en clair : `texteVersHtml` la transforme en lien au
 * moment de l'envoi, si bien que la relecture ne coûte pas le lien.
 */
export function composerMailTrackingTexte(d, 
/**
 * Réglages, pour le modèle. Facultatif : les appelants qui ne les ont pas
 * obtiennent le modèle du code, ce qui est l'ancien comportement exact.
 */
reglages) {
    const langue = normaliserLangueMail(d.langue);
    const m = MODELES[langue];
    const lien = urlSure(d.urlSuivi) ? d.urlSuivi : null;
    /*
     * Le bloc de suivi reste CALCULÉ, et n'est pas offert au réglage.
     *
     * Il change de forme selon ce qu'on a : un lien cliquable quand le
     * transporteur en publie un, le numéro en clair sinon, et la référence
     * répétée seulement dans le premier cas. C'est une logique, pas une phrase —
     * la confier à un texte libre reviendrait à demander à l'utilisateur d'écrire
     * une condition, et un modèle mal repris enverrait au client un lien vide.
     *
     * Le reste du mail — salutation, annonce, formule de politesse — se règle
     * normalement, par `{{suivi}}`.
     */
    const suivi = lien
        ? [`${m.lienIntro} ${lien}`, d.transporteur ? `${m.transporteurLabel} ${d.transporteur}` : null]
        : [
            `${m.numeroIntro} ${d.tracking}`,
            d.transporteur ? `${m.transporteurLabel} ${d.transporteur}` : null,
        ];
    // La ligne « référence » ne se répète pas quand le numéro est déjà en clair.
    // La mise en garde sur l'activation suit la même règle, et pour la même
    // raison : sans lien, elle parlerait d'une page qui n'existe pas.
    if (lien) {
        suivi.push(`${m.reference} ${d.tracking}`);
        suivi.push(m.delaiActivation);
    }
    const modele = modeleMail(reglages, 'tracking_client', langue);
    const valeurs = {
        '{{ref}}': d.ref,
        '{{suivi}}': suivi.filter(Boolean).join('\n'),
    };
    const remplir = (texte) => Object.entries(valeurs).reduce((acc, [cle, v]) => acc.split(cle).join(v), texte);
    return {
        langue,
        objet: remplir(modele.objet),
        corps: remplir(modele.corps).replace(/\n{3,}/g, '\n\n').trim(),
    };
}
// ---------------------------------------------------------------------------
// Mail d'expédition
// ---------------------------------------------------------------------------
/**
 * Annonce du départ, composée ici plutôt que dans n8n.
 *
 * Reprend le scénario « Expédition » : un aiguillage de langue, un bloc de
 * texte, la signature, et l'adresse d'ingestion HubSpot en copie cachée. Deux
 * différences de fond :
 *
 * - **cinq langues** au lieu de deux. Le scénario n'avait que français et
 *   anglais, et son aiguillage envoyait tout le reste en français ;
 * - **la date suit la langue du mail**. Le scénario calculait une locale puis
 *   écrivait `'fr-FR' : 'fr-FR'` dans les deux branches — un client allemand
 *   lisait donc « 29 juin ».
 *
 * Et comme partout désormais : rien ne part sans relecture, le mail est relu à
 * l'écran puis envoyé au clic.
 */
const LOCALES = {
    fr: 'fr-FR', en: 'en-GB', it: 'it-IT', es: 'es-ES', de: 'de-DE',
};
/** « 29 juin », « 29 June », « 29 giugno »… selon la langue du mail. */
export function formatJourLong(jour, langue) {
    if (!jour)
        return '—';
    const [a, m, j] = jour.split('-').map(Number);
    if (!a || !m || !j)
        return '—';
    return new Intl.DateTimeFormat(LOCALES[langue], {
        day: 'numeric', month: 'long', timeZone: 'UTC',
    }).format(new Date(Date.UTC(a, m - 1, j)));
}
const MODELES_EXPEDITION = {
    fr: {
        objet: (ref) => `Expédition Commande ${ref}`,
        corps: [
            'Bonjour,',
            '',
            'Nous vous informons que votre commande a été planifiée en production et sera expédiée le {{date}}.',
            'Il faudra compter par la suite 3 à 5 jours pour la livraison.',
            'Nous ne manquerons pas de vous faire parvenir le lien de suivi.',
            '',
            'Je reste à votre disposition pour tout développement à ce sujet.',
            '',
            'Belle journée à vous,',
            'Cordialement',
        ],
    },
    en: {
        objet: (ref) => `Order shipment ${ref}`,
        corps: [
            'Dear Customer,',
            '',
            'We are pleased to inform you that your order has been scheduled for production and will be shipped on {{date}}.',
            'Please allow 3 to 5 business days for delivery following the shipment.',
            'We will make sure to provide you with the tracking link as soon as it becomes available.',
            '',
            'I remain at your disposal should you require any further information.',
            '',
            'Best regards',
        ],
    },
    it: {
        objet: (ref) => `Spedizione ordine ${ref}`,
        corps: [
            'Buongiorno,',
            '',
            'Le comunichiamo che il suo ordine è stato programmato in produzione e sarà spedito il {{date}}.',
            'Per la consegna occorrono poi dai 3 ai 5 giorni lavorativi.',
            'Non mancheremo di inviarle il link per seguire la spedizione.',
            '',
            'Resto a sua disposizione per ogni ulteriore informazione.',
            '',
            'Cordiali saluti',
        ],
    },
    es: {
        objet: (ref) => `Envío del pedido ${ref}`,
        corps: [
            'Buenos días,',
            '',
            'Le informamos de que su pedido ha sido planificado en producción y se enviará el {{date}}.',
            'Después habrá que contar de 3 a 5 días laborables para la entrega.',
            'No dejaremos de enviarle el enlace de seguimiento.',
            '',
            'Quedo a su disposición para cualquier información adicional.',
            '',
            'Atentamente',
        ],
    },
    de: {
        objet: (ref) => `Versand Ihrer Bestellung ${ref}`,
        corps: [
            'Guten Tag,',
            '',
            'wir teilen Ihnen mit, dass Ihre Bestellung für die Produktion eingeplant wurde und am {{date}} versandt wird.',
            'Für die Zustellung sind anschließend 3 bis 5 Werktage einzuplanen.',
            'Den Link zur Sendungsverfolgung senden wir Ihnen, sobald er vorliegt.',
            '',
            'Für weitere Informationen stehe ich Ihnen gerne zur Verfügung.',
            '',
            'Mit freundlichen Grüßen',
        ],
    },
};
export function composerMailExpeditionTexte(d, 
/** Réglages, pour le modèle. Absents, c'est celui du code qui sert. */
reglages) {
    const langue = normaliserLangueMail(d.langue);
    const date = formatJourLong(d.dateExpedition, langue);
    const modele = modeleMail(reglages, 'expedition_client', langue);
    const valeurs = { '{{ref}}': d.ref, '{{date}}': date };
    const remplir = (texte) => Object.entries(valeurs).reduce((acc, [cle, v]) => acc.split(cle).join(v), texte);
    return {
        langue,
        objet: remplir(modele.objet),
        corps: remplir(modele.corps).replace(/\n{3,}/g, '\n\n').trim(),
    };
}
/** Locale d'écriture des montants, par langue de mail. */
const LOCALE_MONTANT = {
    fr: 'fr-FR', en: 'en-GB', it: 'it-IT', es: 'es-ES', de: 'de-DE',
};
/**
 * Relance de règlement composée en TEXTE, pour être relue avant envoi.
 *
 * Le montant est le reste dû quand Sellsy l'a donné — un acompte versé ne doit
 * pas être réclamé une seconde fois —, le montant de la commande sinon. Il
 * s'écrit dans la langue du client : « 1 200,00 € » n'est pas « €1,200.00 ».
 */
export function composerRelanceReglementTexte(d, 
/** Réglages, pour le modèle. Absents, c'est celui du code qui sert. */
reglages) {
    const langue = normaliserLangueMail(d.langue);
    const montant = (d.resteDu ?? d.montant).toLocaleString(LOCALE_MONTANT[langue], {
        style: 'currency', currency: 'EUR',
    });
    const modele = modeleMail(reglages, 'relance_reglement', langue);
    const valeurs = {
        '{{ref}}': d.ref, '{{client}}': d.client, '{{ville}}': d.ville ?? '', '{{montant}}': montant,
    };
    const remplir = (texte) => Object.entries(valeurs).reduce((acc, [cle, v]) => acc.split(cle).join(v), texte);
    return {
        langue,
        objet: remplir(modele.objet),
        corps: remplir(modele.corps).replace(/\n{3,}/g, '\n\n').trim(),
    };
}
//# sourceMappingURL=mails.js.map